import React, { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, Button, CheckBox, Input, TopProgress, Typography } from '../../../../components'
import { Container } from '../styles'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { clientService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { usePageTitle } from '../../../../context/navigationContext'

const EditUser:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { userId } = useParams()
  const {viewAlert} = useUi()
  usePageTitle(t("PAGE_TITLE_USER_EDIT"))
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [userData, setUserData] = useState<any>(null)

  useEffect(() => {
    if (!userId) return
    const fetchUser = async () => {
      try {
        const res = await clientService.getUsersById(userId)
        setUserData(res.data)
        setPageLoading(false)
      } catch (e: any) {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || t('ERROR_OCCURRED')
        })
        setPageLoading(false)
      }
    }
    fetchUser()
  }, [userId])

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      first_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      last_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      email: yup.string().email(`${t('INVALID_EMAIL')}`).required(`${t('REQUIRED_FIELD')}`),
      external_auth_id: yup.string(),
      password: yup.string().min(6, `${t('MIN_VALUE')} 6`),
      is_staff: yup.boolean(),
      is_active: yup.boolean(),
      email_verified: yup.boolean(),
      is_superuser: yup.boolean(),
    }),
    initialValues: {
      first_name: userData?.first_name || '',
      last_name: userData?.last_name || '',
      email: userData?.email || '',
      external_auth_id: userData?.external_auth_id || '',
      password: '',
      is_staff: userData?.is_staff || false,
      is_active: userData?.is_active ?? true,
      email_verified: userData?.email_verified || false,
      is_superuser: userData?.is_superuser || false,
    },
    onSubmit: (vals:any) => {
      setLoading(true)
      const payload: any = {
        id: userId,
        first_name: vals.first_name,
        last_name: vals.last_name,
        email: vals.email,
        username: vals.email,
        external_auth_id: vals.external_auth_id,
        is_staff: vals.is_staff,
        is_active: vals.is_active,
        email_verified: vals.email_verified,
        is_superuser: vals.is_superuser,
      }
      if (vals.password) {
        payload.password = vals.password
      }
      clientService.editUser(payload)
      .then(() => {
        setLoadingComplete(true)
        setTimeout(() => {
          setLoading(false)
          setLoadingComplete(false)
          navigate(`/clients-users/users/${userId}`)
        }, 800)
      })
      .catch((e:any) => {
        if(e?.response?.data){
          viewAlert({
            severity:'error',
            message: e.response?.data?.detail || t('ERROR_OCCURRED')
          })
        }
        setLoading(false)
      })
    }
  })

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  }

  if (pageLoading) return <TopProgress loading={pageLoading}/>

  return (
    <Container>
      <BackArrow fallbackPath={`/clients-users/users/${userId}`}/>
      <Typography variant='h1'>{t("EDIT_USER")}</Typography>
      <div style={{display:'flex', flexDirection:'column', gap:'16px', maxWidth:'520px', marginTop:'16px'}}>
        <Input
          required
          fieldName='first_name'
          title={t("FIRST_NAME")}
          placeholder=''
          {...formikProps}
        />
        <Input
          required
          fieldName='last_name'
          title={t("LAST_NAME")}
          placeholder=''
          {...formikProps}
        />
        <Input
          required
          fieldName='email'
          title={t("EMAIL")}
          placeholder=''
          {...formikProps}
        />
        <Input
          fieldName='external_auth_id'
          title={t("EXTERNAL_AUTH_ID")}
          placeholder=''
          {...formikProps}
        />
        <Input
          fieldName='password'
          title={t("PASSWORD")}
          type='password'
          placeholder={t("LEAVE_EMPTY_TO_KEEP_CURRENT")}
          {...formikProps}
        />
        <CheckBox
          title={t("STAFF_USER")}
          value={values.is_staff}
          onChange={() => setFieldValue('is_staff', !values.is_staff)}
        />
        <CheckBox
          title={t("IS_ACTIVE")}
          value={values.is_active}
          onChange={() => setFieldValue('is_active', !values.is_active)}
        />
        <CheckBox
          title={t("EMAIL_VERIFIED")}
          value={values.email_verified}
          onChange={() => setFieldValue('email_verified', !values.email_verified)}
        />
        <CheckBox
          title={t("IS_SUPERUSER")}
          value={values.is_superuser}
          onChange={() => setFieldValue('is_superuser', !values.is_superuser)}
        />
        <div style={{display:'flex', gap:'10px', marginTop:'8px'}}>
          <Button
            variant="primary"
            size="display"
            onClick={handleSubmit}
            loading={loading}
            loadingComplete={loadingComplete}
            title={t("SAVE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate(`/clients-users/users/${userId}`)}
            title={t("CANCEL")}
          />
        </div>
      </div>
      <TopProgress loading={loading}/>
    </Container>
  )
}

export default EditUser
